// components/AssigneePicker.tsx
import React from 'react';
import { StarIcon } from '@heroicons/react/20/solid';
import { Avatar, cx, focusRing } from './ui';

interface AssigneePickerProps {
  members: { id: string; name: string }[];
  assignments: { userId: string; isResponsible: boolean }[];
  toggleAssignment: (userId: string) => void;
  toggleResponsible: (userId: string) => void;
}

/** The household's people as chips: tap one to assign them, and the star to make them primary. */
const AssigneePicker: React.FC<AssigneePickerProps> = ({ members, assignments, toggleAssignment, toggleResponsible }) => {
  if (members.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Pick a household to see its members.</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {members.map(member => {
        const assignment = assignments.find(a => a.userId === member.id);
        const assigned = !!assignment;
        return (
          <span
            key={member.id}
            className={cx(
              'inline-flex h-9 items-center rounded-full border transition-colors',
              assigned ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-400/40 dark:bg-indigo-500/15'
                : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700',
            )}
          >
            <button
              type="button"
              aria-pressed={assigned}
              onClick={() => toggleAssignment(member.id)}
              className={cx(
                'inline-flex h-full items-center gap-2 rounded-full pl-1 text-sm font-medium',
                assigned ? 'pr-1.5 text-indigo-800 dark:text-indigo-200' : 'pr-3 text-gray-700 dark:text-gray-200',
                focusRing,
              )}
            >
              <Avatar name={member.name} id={member.id} />
              {member.name}
            </button>
            {assigned && (
              <button
                type="button"
                aria-pressed={assignment.isResponsible}
                aria-label={`Make ${member.name} primary`}
                title={assignment.isResponsible ? 'Primary: the one who usually does it' : 'Make primary'}
                onClick={() => toggleResponsible(member.id)}
                className={cx(
                  'mr-1 inline-flex h-7 items-center gap-1 rounded-full px-2 text-xs font-semibold transition-colors',
                  assignment.isResponsible
                    ? 'bg-white text-indigo-700 ring-1 ring-indigo-200 dark:bg-gray-900 dark:text-indigo-300 dark:ring-indigo-400/40'
                    : 'text-indigo-400 hover:bg-white hover:text-indigo-700 dark:hover:bg-gray-900 dark:hover:text-indigo-300',
                  focusRing,
                )}
              >
                <StarIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {assignment.isResponsible && 'Primary'}
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
};

export default AssigneePicker;
